/* global google */
/// <reference types="googlemaps" />

import { GoogleMap, OverlayView, Marker, useLoadScript } from "@react-google-maps/api";
import { makeStyles } from "@material-ui/core";
import { isEmpty, isObject, isPlainObject } from "lodash";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import SiteAlertsInfo from "./SiteAlertsInfo";
export interface IAlertMarker {
  id: string;
  site: string;
  pos: google.maps.LatLng | any;
  color: string;
  severity: number;
  siteAlerts: any;
}

const useStyles = makeStyles({
  mapContainer: {
    height: "100%",
    width: "auto",
    borderRadius: 4,
    "& .gm-bundled-control-on-bottom": {
      marginRight: "20px !important",    
    }
  }
});

const AlertsMap: any = ({filterType, setFilterdAlertsCounts}: any) => {
  const classes = useStyles();
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<IAlertMarker[]>([]);
  const [infoOpen, setInfoOpen] = useState<string | undefined>(undefined);
  const selections = useStoreState((s) => s.selections.selections);
  const updateSelections = useStoreActions((s) => s.selections.updateSelections);
  const [shouldCenterMap, setShouldCenterMap] = useState<boolean>(true);
  const [currentSiteId, setCurrentSiteId] = useState<string>("");
  const { siteId, customerId } = selections;
  const getSiteMapSummary = useStoreActions((actions) => actions.sites.getSiteMapSummary);
  const getCustomerMapSummary = useStoreActions((actions) => actions.customers.getCustomerMapSummary);
  const eventCategoriesData =  useStoreState((state) => state.types.eventCategoriesData);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const userPref = useStoreState((s) => s.users.userPreferences);

  const filterTypekey = useMemo(() => {
    return Object.keys(eventCategoriesData).find(
      (key) => eventCategoriesData[key].id === filterType
    ) || "";
  }, [eventCategoriesData, filterType]);

  const closeInfoWindow = () => {
    setInfoOpen(undefined);
  };

  const onAlertClick = useCallback((marker: any) => {
    const siteId = marker.id;
    setShouldCenterMap(false);
    if (mapRef) {
      mapRef.setCenter({ lat: marker.pos.lat, lng: marker.pos.lng });
    }
    setInfoOpen((infoOpen) => (infoOpen === siteId ? undefined : siteId));
  }, [mapRef]);

  const onMapLoad = (map: google.maps.Map) => {
    // Remember map reference
    setMapRef(map);
  };

  const getMaxSeverityEvent = (eventCount: []) => {
    // Filter out keys with zero values
    const nonZeroKeys = Object.entries(eventCount || {})
      .filter(([_, value]) => value > 0)
      .map(([key]) => Number(key));

    // If no non-zero keys, return null
    if (nonZeroKeys.length === 0) return null;

    // Find the key with the maximum severity
    const maxKey = nonZeroKeys.reduce((maxKey, currentKey) => {
      return eventCategoriesData[currentKey]?.severity > eventCategoriesData[maxKey]?.severity ? currentKey : maxKey;
    });
    // Return the corresponding event category data
    return eventCategoriesData[maxKey];
  };

  const calculateFilteredEventCounts = (sites: any[]) => {
    const filterdAlertsCounts =  sites.reduce((acc, site) => {
      const count = site.eventCount?.[filterTypekey];
      if (count > 0) {
        acc.push({ siteId:site.id, name: site.name, eventCount: count, filterType: filterTypekey});
      }
      return acc;
    }, []);

    setFilterdAlertsCounts(filterdAlertsCounts);
  }

  const createMarker = ({ id, name, lat, lon, eventCount }: any) => {
    const maxSeverityEvent = getMaxSeverityEvent(eventCount);
    return {
      id,
      site: name,
      siteAlerts: eventCount,
      pos: { lat: lat || 0 , lng: lon || 0 },
      color: maxSeverityEvent?.color ?? '#999',
      severity: maxSeverityEvent?.severity ?? 0,
    };
  };

  // A site without the dashboard package gets `[]` back instead of a summary object,
  // so only entries that actually carry event counts can become markers.
  const isSiteSummary = (summary: unknown) =>
    isPlainObject(summary) && isObject((summary as { eventCount?: unknown }).eventCount);

  const fetchData = async (isStale: () => boolean) => {
    if (!siteId && !customerId) {
      return;
    }
    setMarkers([]);
    setSummaryData([]);
    try {
      let res ;
      if (siteId) {
        res = await getSiteMapSummary(siteId);
        if (isStale()) {
          return;
        }
        setSummaryData(isSiteSummary(res) ? [res] : []);
      } else if(customerId) {
        res = await getCustomerMapSummary(customerId);
        if (isStale()) {
          return;
        }
        setSummaryData(Array.isArray(res) ? res.filter(isSiteSummary) : []);
      } else {
        return;
      }
    } catch (error) {
      console.error('Error fetching power state data:', error);
    }
  };

  useEffect(() => {
    // The selection can change while a request is in flight (e.g. the header drops a site
    // that has no dashboard access), so a late response must not overwrite the newer one.
    let isActive = true;
    fetchData(() => !isActive);

    return () => {
      isActive = false;
    };
  }, [siteId, customerId, userPref]);

  useEffect(() => {
    if (!isEmpty(summaryData)) {
      const newMarkers = summaryData.filter(data => !!data).map(createMarker);
      setMarkers(newMarkers);
    }
  }, [summaryData]);

  useEffect(() => {
    if(isEmpty(summaryData) || isEmpty(filterType)) {
      return;
    }
    calculateFilteredEventCounts(summaryData);
  }, [summaryData, filterType]);

  // Fit map bounds according to markers
  const fitMapToMarkers = () => {
    if (mapRef) {
      const bounds = new google.maps.LatLngBounds();
      if (bounds) {
        markers.forEach((marker) => {
          bounds.extend(marker.pos);
        });
        mapRef.fitBounds(bounds);
        // mapRef.setCenter(bounds.getCenter());
        mapRef.panToBounds(bounds);
      }
    }
  };

  useEffect(() => {
    const newId = selections.siteId || selections.customerId;
    if (newId && currentSiteId !== newId) {
      // On each site change enable again reseting marks
      setShouldCenterMap(true);
      setCurrentSiteId(newId);
    }

    // handle clicked same marker twise
    if (currentSiteId === newId && !shouldCenterMap) {
      setShouldCenterMap(true);
    }
    // Closing any open info about any marker

    if (infoOpen && (selections.siteId !== infoOpen)) {
      setInfoOpen(undefined);
    }
  }, [selections]);


  const goToSiteDashboard = useCallback((siteId: string) => {
    updateSelections({ type: "site", data: siteId });
  }, [updateSelections]);

  // Fit map as soon as we get new markers
  useEffect(() => {
    if (mapRef) { fitMapToMarkers(); }
  }, [markers]);

  // Load the Google maps scripts
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBJIN2UpLEwA0WENCrw5eahjzwq7aMzRBY",
    language: "en"
  });

  const getPixelPositionOffset = useCallback((width = 0, height = 0) => ({
    x: -(width / 2 - 40),
    y: -height + 10,
  }), []);

  return (
    <Fragment>
      {isLoaded ? (
        <GoogleMap
          id="alerts-map"
          mapContainerClassName={classes.mapContainer}
          onLoad={onMapLoad}
          options={{
            minZoom: 3,
            maxZoom: 16,
            zoomControl: true, // +/- zoom buttons
            disableDefaultUI: true,
            styles: [
              {},

              {
                featureType: 'poi',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'administrative',
                stylers: [{ visibility: 'simplified' }],
              },
              {
                featureType: 'transit',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'road',
                stylers: [{ visibility: 'simplified' /*'off'*/ }],
              },
            ],
          }}
        >
          {markers.map((marker, id) => (
            <Fragment key={id}>
              <Marker
                position={marker.pos}
                onClick={() => {
                  onAlertClick(marker);
                }}
                animation={google.maps.Animation.DROP}
                icon={{
                  url:
                    "data:image/svg+xml;charset=utf-8," +
                    encodeURIComponent(
                      marker.severity
                        ? `<svg viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="50" cy="50" r="50" stroke="none" fill="${marker.color}" fill-opacity="20%" />
                          <circle cx="50" cy="50" r="30" stroke="none" fill="${marker.color}" fill-opacity="30%" />
                          <circle cx="50" cy="50" r="10" stroke="none" fill="${marker.color}" fill-opacity="40%" />
                          </svg>`
                        : `<svg viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="50" cy="50" r="30" stroke="none" fill="${marker.color}" fill-opacity="30%" />
                            <circle cx="50" cy="50" r="10" stroke="none" fill="${marker.color}" fill-opacity="40%" />
                            </svg>`
                    ),
                  size: new google.maps.Size(200, 200),
                  scaledSize: new google.maps.Size(128, 128),
                  anchor: new google.maps.Point(29, 30),
                }}
              />
              {infoOpen === marker.id && (
                <OverlayView
                  position={marker.pos}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={getPixelPositionOffset}
                >
                  <SiteAlertsInfo
                    handleClose={closeInfoWindow}
                    marker={marker}
                    goToSiteDashboard={goToSiteDashboard}
                  />
                </OverlayView>
              )}
            </Fragment>
          ))}
        </GoogleMap>
      ) : (
        'Loading map...'
      )}
    </Fragment>
  );
};

export default AlertsMap;
